import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { userSlice } from '../store/user';
import { selectAccessToken, selectRefreshToken } from '../store/user/selectors';

export interface TokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
}

export interface TokenObject {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  tokenType: string;
}

export const transformTokenResponse = ({
  access_token: accessToken,
  expires_in: expiresIn,
  refresh_token: refreshToken,
  token_type: tokenType,
}: TokenResponse) => ({
  accessToken,
  expiresIn,
  refreshToken,
  tokenType,
});

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    headers.set('Accept', `application/json`);

    const accessToken = selectAccessToken(getState() as RootState);

    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  },
});

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: async (args, api, extraOptions) => {
    const accessToken = selectAccessToken(api.getState() as RootState);
    const refreshToken = selectRefreshToken(api.getState() as RootState);

    if (!accessToken && refreshToken) {
      const formData = new FormData();
      formData.append('grant_type', 'refresh_token');
      formData.append('refresh_token', refreshToken);

      const refreshResult = await baseQuery(
        {
          method: 'POST',
          url: '/oauth/v2/token',
          body: formData,
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const data = transformTokenResponse(
          refreshResult.data as TokenResponse
        );
        api.dispatch(userSlice.actions.setTokenData(data));

        return baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(userSlice.actions.deleteTokenData());
      }
    }

    return baseQuery(args, api, extraOptions);
  },
  tagTypes: ['Apps'],
  endpoints: () => ({}),
});
